import React, { useState, useCallback, memo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  Alert,
  Spinner,
} from "reactstrap";

const MessageBubble = memo(function MessageBubble({ msg, onOpenJson }) {
  return (
    <div
      style={{
        marginBottom: "1rem",
        display: "flex",
        justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
      }}
    >
      <div
        role={msg.rawData ? "button" : undefined}
        tabIndex={msg.rawData ? 0 : undefined}
        onClick={() => {
          if (msg.rawData) onOpenJson(msg.rawData);
        }}
        onKeyDown={(e) => {
          if (msg.rawData && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onOpenJson(msg.rawData);
          }
        }}
        className={msg.rawData ? "agent-test-msg--clickable" : undefined}
        style={{
          maxWidth: "70%",
          padding: "0.75rem 1rem",
          borderRadius: "1rem",
          backgroundColor:
            msg.role === "user"
              ? "#007bff"
              : msg.role === "error"
              ? "#dc3545"
              : "#ffffff",
          color:
            msg.role === "user" || msg.role === "error" ? "#ffffff" : "#000000",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          cursor: msg.rawData ? "pointer" : "default",
          position: "relative",
        }}
      >
        {msg.role === "system" && (
          <small className="d-block mb-1" style={{ opacity: 0.8 }}>
            <i className="fas fa-info-circle me-1"></i>
            Sistema
          </small>
        )}
        {msg.role === "error" && (
          <small className="d-block mb-1" style={{ opacity: 0.8 }}>
            <i className="fas fa-exclamation-circle me-1"></i>
            Error
          </small>
        )}
        {msg.rawData && (
          <small
            className="d-block mb-1"
            style={{
              opacity: 0.7,
              fontSize: "0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
            }}
          >
            <i className="fas fa-code me-1"></i>
            Haz clic para ver JSON
          </small>
        )}
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {msg.content}
        </div>
      </div>
    </div>
  );
});

/**
 * Local input state keeps typing from re-rendering the parent Agentes tree.
 */
const AgentTestModal = ({
  isOpen,
  onClose,
  testInitializing,
  testError,
  testMessages,
  testLoading,
  testSessionId,
  onSend,
}) => {
  const [draftInput, setDraftInput] = useState("");
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [selectedMessageJson, setSelectedMessageJson] = useState(null);

  const handleSend = useCallback(() => {
    const text = draftInput.trim();
    if (!text) return;
    setDraftInput("");
    onSend(text);
  }, [draftInput, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openJson = useCallback((raw) => {
    setSelectedMessageJson(raw);
    setJsonModalOpen(true);
  }, []);

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static">
        <ModalHeader toggle={onClose}>
          <i className="fas fa-vial me-2"></i>
          Probar Agente
        </ModalHeader>
        <ModalBody
          style={{
            padding: "0",
            display: "flex",
            flexDirection: "column",
            height: "500px",
            position: "relative",
          }}
        >
          {testInitializing && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                borderRadius: "0.25rem",
              }}
            >
              <Spinner
                color="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p
                className="mt-3 mb-0"
                style={{ fontSize: "1.1rem", fontWeight: "500" }}
              >
                Inicializando sesión de prueba...
              </p>
            </div>
          )}

          {testError && (
            <Alert color="danger" className="m-3 mb-0">
              {testError}
            </Alert>
          )}

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "1rem",
              backgroundColor: "#f8f9fa",
            }}
          >
            {testMessages.length === 0 && !testInitializing && (
              <div className="text-center text-muted mt-5">
                <i className="fas fa-comments fa-3x mb-3"></i>
                <p>Inicia una conversación para probar el agente</p>
              </div>
            )}
            {testMessages.map((msg, index) => (
              <MessageBubble
                key={index}
                msg={msg}
                onOpenJson={openJson}
              />
            ))}
            {testLoading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div
                  style={{
                    padding: "0.75rem 1rem",
                    borderRadius: "1rem",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  <Spinner size="sm" className="me-2" />
                  <span className="text-muted">El agente está pensando...</span>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              borderTop: "1px solid #dee2e6",
              padding: "1rem",
              backgroundColor: "#ffffff",
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Input
                type="textarea"
                rows="2"
                value={draftInput}
                onChange={(e) => setDraftInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe un mensaje para probar el agente..."
                disabled={testLoading || testInitializing || !testSessionId}
                style={{ resize: "none" }}
              />
              <Button
                color="primary"
                onClick={handleSend}
                disabled={
                  testLoading ||
                  testInitializing ||
                  !testSessionId ||
                  !draftInput.trim()
                }
                style={{ alignSelf: "flex-end" }}
              >
                {testLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </Button>
            </div>
            <small className="text-muted d-block mt-2">
              <i className="fas fa-info-circle me-1"></i>
              Esta es una prueba local. El agente no está desplegado.
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={jsonModalOpen}
        toggle={() => setJsonModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setJsonModalOpen(false)}>
          <i className="fas fa-code me-2"></i>
          Detalles de la Respuesta (JSON)
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {selectedMessageJson && (
            <pre
              style={{
                backgroundColor: "#f8f9fa",
                padding: "1rem",
                borderRadius: "0.25rem",
                border: "1px solid #dee2e6",
                fontSize: "0.875rem",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(selectedMessageJson, null, 2)}
            </pre>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              if (selectedMessageJson) {
                navigator.clipboard.writeText(
                  JSON.stringify(selectedMessageJson, null, 2)
                );
                alert("JSON copiado al portapapeles");
              }
            }}
          >
            <i className="fas fa-copy me-2"></i>
            Copiar JSON
          </Button>
          <Button color="secondary" onClick={() => setJsonModalOpen(false)}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .agent-test-msg--clickable:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          transform: translateY(-1px);
        }
      `}</style>
    </>
  );
};

export default AgentTestModal;
